import React, { useCallback, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'

import InstallationListGroup from './installation_list_group'
import DeleteConfirmationModal from '../../shared/delete_confirmation_modal'
import StringInputModal from '../../shared/string_input_modal'
import LocationStr from '../../../location_str'
import { useDatabase } from '../../../../providers/database_provider'
import {
    type InstallationDocument,
    useInstallations,
} from '../../../../providers/installations_provider'
import { useProject } from '../../../../providers/project_provider'
import { useWorkflow } from '../../../../providers/workflow_provider'
import templates from '../../../../templates'
import { type Installation } from '../../../../types/database.types'
import {
    newInstallation,
    putInstallation,
    removeInstallation,
    setDocumentName,
} from '../../../../utilities/database_utils'
import { someLocation } from '../../../../utilities/location_utils'
import { type Validator } from '../../../../utilities/validation_utils'
import ImportChildDoc from './import_child_document'

interface JobListProps {
    workflowName: string | undefined
}

const JobList: React.FC<JobListProps> = ({ workflowName }) => {
    const db = useDatabase()

    const navigate = useNavigate()

    const [project, , reloadProject] = useProject()

    const workflow = useWorkflow()

    const [installations, setInstallations, reloadInstallations] =
        useInstallations()

    const [installationForAddModalValue, setInstallationForAddModalValue] =
        useState<string>('')
    const [
        installationForRenameModalValue,
        setInstallationForRenameModalValue,
    ] = useState<string | undefined>(undefined)
    const [
        isInstallationForAddModalVisible,
        setIsInstallationForAddModalVisible,
    ] = useState<boolean>(false)
    const [selectedInstallationForDelete, setSelectedInstallationForDelete] =
        useState<InstallationDocument | undefined>(undefined)
    const [selectedInstallationForRename, setSelectedInstallationForRename] =
        useState<InstallationDocument | undefined>(undefined)

    const installationDocumentNames = useMemo<Array<string>>(() => {
        return installations.map(installation => {
            return installation.metadata_.doc_name
        })
    }, [installations])

    const installationNameValidators = useMemo<Array<Validator<string>>>(() => {
        const re =
            /^(?:A[LKZR]|C[AOT]|DE|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEHINOST]|N[CDEHJMVY]|O[HKR]|PA|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY]) ?(?:[a-z0-9](?:[ -]?[a-z0-9])*){0,62}$/i

        return [
            input => {
                if (re.test(input)) {
                    return undefined
                } else {
                    return 'The home identification code must consist of the two-digital postal abbreviation for the state where the home resides, and a unique code consisting of no more than 64 characters including letters, numbers, dashes, and single spaces. Single spaces can only appear between other characters'
                }
            },
            input => {
                if (installationDocumentNames.includes(input.trim())) {
                    return 'Home identification code already exists. Please provide a different value.'
                } else {
                    return undefined
                }
            },
        ]
    }, [installationDocumentNames])

    // @note Implementation of {handleConfirmInstallationForAdd} function.
    //     After the PouchDB document for the new installation has been inserted
    //     into the database, there are 2 possible behaviors: reload the current
    //     list of installations, or navigate to the new installation. Both
    //     behaviors are implemented. Currently, the code for the first behavior
    //     is commented out.
    //
    //     To revert to the first behavior, uncomment the lines for the 4
    //     function calls and the dependencies on the {reloadProject} and
    //     {reloadInstallations} functions in the call to {React.useCallback}
    //     function, and then comment out the call to the {navigate} function.
    const handleConfirmInstallationForAdd = useCallback(async () => {
        if (project && workflowName) {
            const installation = newInstallation(
                installationForAddModalValue.trim(),
                workflowName,
                templates[workflowName]?.title || '',
                undefined,
            )

            await putInstallation(db, project._id, installation)

            // await reloadInstallations()
            //
            // await reloadProject()
            //
            // setIsInstallationForAddModalVisible(false)
            //
            // setInstallationForAddModalValue('')

            navigate(
                `/app/${project._id}/${workflowName}/${installation._id}`,
                {
                    replace: true,
                },
            )
        }
    }, [
        db,
        installationForAddModalValue,
        navigate,
        project,
        // reloadInstallations,
        // reloadProject,
        workflowName,
    ])

    const handleConfirmInstallationForDelete = useCallback(async () => {
        if (project && selectedInstallationForDelete) {
            await removeInstallation(
                db,
                project._id,
                selectedInstallationForDelete._id,
                selectedInstallationForDelete._rev,
            )

            setInstallations(previousInstallations => {
                return previousInstallations.filter(previousInstallation => {
                    return (
                        previousInstallation._id !==
                        selectedInstallationForDelete._id
                    )
                })
            })

            await reloadProject()

            setSelectedInstallationForDelete(undefined)
        }
    }, [
        db,
        project,
        reloadProject,
        selectedInstallationForDelete,
        setInstallations,
    ])

    const handleConfirmInstallationForRename = useCallback(async () => {
        if (selectedInstallationForRename && installationForRenameModalValue) {
            await setDocumentName<Installation>(
                db,
                selectedInstallationForRename._id,
                installationForRenameModalValue.trim(),
            )

            await reloadInstallations()

            setSelectedInstallationForRename(undefined)

            setInstallationForRenameModalValue(undefined)
        }
    }, [
        db,
        installationForRenameModalValue,
        reloadInstallations,
        selectedInstallationForRename,
    ])

    return (
        <>
            <StringInputModal
                title="Enter a new home identification code"
                cancelLabel="Cancel"
                confirmLabel="Add"
                value={installationForAddModalValue}
                validators={installationNameValidators}
                show={isInstallationForAddModalVisible}
                onHide={() => {
                    setIsInstallationForAddModalVisible(false)

                    setInstallationForAddModalValue('')
                }}
                onCancel={() => {
                    setIsInstallationForAddModalVisible(false)

                    setInstallationForAddModalValue('')
                }}
                onConfirm={handleConfirmInstallationForAdd}
                onChange={value => {
                    setInstallationForAddModalValue(value)
                }}
            />
            <StringInputModal
                title="Enter a new home identification code"
                cancelLabel="Cancel"
                confirmLabel="Rename"
                value={installationForRenameModalValue}
                validators={installationNameValidators}
                show={selectedInstallationForRename !== undefined}
                onHide={() => {
                    setSelectedInstallationForRename(undefined)

                    setInstallationForRenameModalValue(undefined)
                }}
                onCancel={() => {
                    setSelectedInstallationForRename(undefined)

                    setInstallationForRenameModalValue(undefined)
                }}
                onConfirm={handleConfirmInstallationForRename}
                onChange={value => {
                    setInstallationForRenameModalValue(value)
                }}
            />
            <DeleteConfirmationModal
                label={selectedInstallationForDelete?.metadata_.doc_name ?? ''}
                show={selectedInstallationForDelete !== undefined}
                onHide={() => {
                    setSelectedInstallationForDelete(undefined)
                }}
                onCancel={() => {
                    setSelectedInstallationForDelete(undefined)
                }}
                onConfirm={handleConfirmInstallationForDelete}
            />
            {project && workflow && (
                <div className="container">
                    <h1>{workflow.title}</h1>
                    {project.metadata_.doc_name != 'Default' && (
                        <h2>For {project.metadata_.doc_name}</h2>
                    )}
                    {project.data_.location &&
                        someLocation(project.data_.location) && (
                            <p className="address">
                                <LocationStr
                                    location={project.data_.location}
                                    separators={[', ', ', ', ' ']}
                                />
                            </p>
                        )}
                    <Button
                        variant="primary"
                        onClick={() => {
                            setIsInstallationForAddModalVisible(true)
                        }}
                    >
                        Add Site Visit
                    </Button>
                    &nbsp;&nbsp;
                    <ImportChildDoc
                        parentId={project._id}
                        label="Import Site Visit"
                        onImport={async () => {
                            await reloadInstallations()
                        }}
                    />
                    <div className="padding">
                        <i>
                            Add and import new site visits (e.g., homes) or
                            access existing ones. After completing a data
                            collection form on the next screen, it can be
                            downloaded from this page for emailing to PNNL.
                        </i>
                    </div>
                    {installations.length > 0 && (
                        <>
                            <div>
                                {installations.map(installation => (
                                    <InstallationListGroup
                                        key={installation._id}
                                        installation={installation}
                                        to={`/app/${project._id}/${installation.metadata_.template_name}/${installation._id}`}
                                        onEdit={() => {
                                            setSelectedInstallationForRename(
                                                installation,
                                            )

                                            setInstallationForRenameModalValue(
                                                installation.metadata_.doc_name,
                                            )
                                        }}
                                        onDelete={() => {
                                            setSelectedInstallationForDelete(
                                                installation,
                                            )
                                        }}
                                    >
                                        {installation.metadata_.doc_name}
                                        <div className="last-modified">
                                            Last Modified:{' '}
                                            {new Date(
                                                installation?.metadata_.last_modified_at,
                                            ).toLocaleString()}
                                        </div>
                                    </InstallationListGroup>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    )
}

export default JobList
